/**
 * The HTTP client for `/api`.
 *
 * Same origin as the page in production (FastAPI serves `dist/`), and proxied
 * to the backend by Vite in development, so there is no base URL to configure
 * and no CORS. The session rides on a cookie; the browser never holds a token.
 */
import type {
  Api,
  Batch,
  BatchPatch,
  FilmedChannel,
  Idea,
  IdeaPatch,
  IdeaWithEvents,
  MatchResult,
  NewBatch,
  NewIdea,
  Placement,
} from './types'

/** A response the server understood and refused. */
export class ApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }

  get isUnauthenticated(): boolean {
    return this.status === 401
  }
}

/** Where a signed-out browser is sent. Google sign-in, handled by the server. */
export const SIGN_IN_PATH = '/auth/login'

/**
 * `DEV_AUTH=1` on the backend signs in as Martin without Google. In `pnpm dev`
 * a 401 means the cookie has not been minted yet, so the client asks for one
 * and retries once, instead of sending a developer through a Google screen
 * that is not configured locally. Never compiled into a production build.
 */
const DEV_SIGN_IN_PATH = '/auth/dev'

export interface Transport {
  fetch: typeof fetch
  /** Whether a 401 may be answered by `/auth/dev`. */
  devAuth: boolean
}

const defaultTransport = (): Transport => ({
  fetch: (...args) => globalThis.fetch(...args),
  devAuth: import.meta.env.DEV,
})

export function createHttpApi(transport: Transport = defaultTransport()): Api {
  let devSignIn: Promise<boolean> | null = null

  async function signInForDevelopment(): Promise<boolean> {
    // One attempt shared by every request that hit the same 401 at once.
    devSignIn ??= transport
      .fetch(DEV_SIGN_IN_PATH, { credentials: 'same-origin', redirect: 'follow' })
      .then((response) => response.ok || response.redirected)
      .catch(() => false)
      .finally(() => {
        devSignIn = null
      })
    return devSignIn
  }

  async function request<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
    const response = await transport.fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(typeof init.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    })

    if (response.status === 401 && transport.devAuth && !retried) {
      if (await signInForDevelopment()) return request<T>(path, init, true)
    }

    if (!response.ok) throw new ApiError(await errorDetail(response), response.status)
    if (response.status === 204) return undefined as T
    return (await response.json()) as T
  }

  const json = (method: string, body?: unknown): RequestInit => ({
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  return {
    listIdeas: (query = {}) => request<Idea[]>(`/api/ideas${queryString({ ...query })}`),
    createIdea: (input: NewIdea) => request<Idea>('/api/ideas', json('POST', input)),
    getIdea: (id) => request<IdeaWithEvents>(`/api/ideas/${id}`),
    // The server answers a PATCH with the activity log attached, as for GET;
    // the list keeps ideas without it, and the detail screen fetches its own.
    updateIdea: (id, patch: IdeaPatch) =>
      request<Idea & { events?: unknown }>(`/api/ideas/${id}`, json('PATCH', patch)).then(
        ({ events, ...idea }) => idea,
      ),
    arrange: (placements: Placement[]) =>
      request<Idea[]>('/api/canvas', json('PATCH', { ideas: placements })),
    deleteIdea: (id) => request<void>(`/api/ideas/${id}`, json('DELETE')),
    createBatch: (input: NewBatch) => request<Batch>('/api/batches', json('POST', input)),
    getBatch: (id) => request<Batch>(`/api/batches/${id}`),
    updateBatch: (id, patch: BatchPatch) =>
      request<Batch>(`/api/batches/${id}`, json('PATCH', patch)),
    listBatches: (options = {}) =>
      request<Batch[]>(`/api/batches${queryString({ open: options.open })}`),
    match: (questions) => request<MatchResult[]>('/api/match', json('POST', { questions })),
  }
}

/** `?a=1&b=2` for the defined, non-empty values, or nothing at all. */
export function queryString(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue
    search.set(key, String(value))
  }
  const text = search.toString()
  return text ? `?${text}` : ''
}

/** FastAPI says why in `detail`: a string, or a list of validation errors. */
async function errorDetail(response: Response): Promise<string> {
  const fallback = `Request failed (${response.status})`
  try {
    const body = (await response.json()) as { detail?: unknown }
    if (typeof body.detail === 'string') return body.detail
    if (Array.isArray(body.detail)) {
      const messages = body.detail
        .map((item) => (item && typeof item === 'object' && 'msg' in item ? String(item.msg) : ''))
        .filter(Boolean)
      if (messages.length) return messages.join('; ')
    }
  } catch {
    // A body that is not JSON is not worth a second error.
  }
  return fallback
}

/**
 * Where the browser can play one of an idea's videos from.
 *
 * The stored path is on the server's disk, not a URL, so the file is always
 * asked for at `/api/ideas/{id}/reels/{channel}`. That URL does not change
 * when a better cut replaces the file, so the path rides along as `v`: the
 * server ignores it and the browser treats it as a different video.
 */
export function reelSource(idea: Pick<Idea, 'id' | 'reels'>, channel: FilmedChannel): string | null {
  const path = idea.reels[channel]
  if (!path) return null
  return `/api/ideas/${idea.id}/reels/${channel}?v=${encodeURIComponent(path)}`
}
